import { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import AdminLayout from '../AdminLayout';
import { API } from '../../../lib/api';
import { toast } from 'sonner';
import {
  Navigation, MessageSquare, Bell, Mail, Smartphone, RefreshCw,
  ChevronUp, ChevronDown, Check, X, AlertTriangle, Settings,
  ArrowRight, Shield, Zap, Save, Play, Search
} from 'lucide-react';

// Channel icons mapping
const CHANNEL_ICONS = {
  push: Bell,
  whatsapp: MessageSquare,
  email: Mail,
  sms: Smartphone,
};

const CHANNEL_LABELS = {
  push: 'Push Notification',
  whatsapp: 'WhatsApp',
  email: 'Email',
  sms: 'SMS',
};

const CHANNEL_COLORS = {
  push: 'bg-purple-100 text-purple-700 border-purple-200',
  whatsapp: 'bg-green-100 text-green-700 border-green-200',
  email: 'bg-blue-100 text-blue-700 border-blue-200',
  sms: 'bg-orange-100 text-orange-700 border-orange-200',
};

// Channel Priority Card
const ChannelPriorityCard = ({ channel, priority, enabled, fallbackEnabled, onUpdate, onMoveUp, onMoveDown, canMoveUp, canMoveDown }) => {
  const Icon = CHANNEL_ICONS[channel] || MessageSquare;
  const label = CHANNEL_LABELS[channel] || channel;
  const colorClass = CHANNEL_COLORS[channel] || 'bg-gray-100 text-gray-700';
  
  return (
    <div className={`flex items-center justify-between p-4 rounded-lg border ${enabled ? colorClass : 'bg-gray-50 border-gray-200 opacity-60'}`}>
      <div className="flex items-center gap-3">
        <div className="flex flex-col gap-1">
          <button 
            onClick={onMoveUp} 
            disabled={!canMoveUp}
            className="p-1 hover:bg-white/50 rounded disabled:opacity-30"
          >
            <ChevronUp className="w-4 h-4" />
          </button>
          <button 
            onClick={onMoveDown} 
            disabled={!canMoveDown}
            className="p-1 hover:bg-white/50 rounded disabled:opacity-30"
          >
            <ChevronDown className="w-4 h-4" />
          </button>
        </div>
        
        <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm">
          <Icon className="w-5 h-5" />
        </div>
        
        <div>
          <p className="font-medium">{label}</p>
          <p className="text-xs opacity-75">Priorità: {priority}</p>
        </div>
      </div>
      
      <div className="flex items-center gap-4">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={fallbackEnabled}
            onChange={(e) => onUpdate({ fallback_enabled: e.target.checked })}
            className="rounded border-gray-300"
          />
          <span>Fallback</span>
        </label>
        
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => onUpdate({ enabled: e.target.checked })}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
        </label>
      </div>
    </div>
  );
};

// Channels Status Panel
const ChannelsStatusPanel = ({ channelsStatus }) => {
  if (!channelsStatus) return null;
  
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
        <Zap className="w-4 h-4 text-amber-500" />
        Stato Canali
      </h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {Object.entries(channelsStatus).map(([channel, status]) => {
          const Icon = CHANNEL_ICONS[channel] || MessageSquare;
          const isConfigured = status.configured;
          const isEnabled = status.enabled;
          
          return (
            <div 
              key={channel}
              className={`p-3 rounded-lg border ${isConfigured && isEnabled ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}
            >
              <div className="flex items-center justify-between mb-2">
                <Icon className={`w-5 h-5 ${isConfigured ? 'text-green-600' : 'text-gray-400'}`} />
                {isConfigured && isEnabled ? (
                  <Check className="w-4 h-4 text-green-600" />
                ) : (
                  <X className="w-4 h-4 text-gray-400" />
                )}
              </div>
              <p className="text-sm font-medium text-gray-900">{CHANNEL_LABELS[channel] || channel}</p>
              <p className="text-xs text-gray-500">
                {status.status === 'not_configured' ? 'Non configurato' : 
                 status.status === 'not_available' ? 'Non disponibile' :
                 status.status === 'healthy' ? 'Attivo' : status.status}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Routing Test Panel
const RoutingTestPanel = ({ token }) => {
  const [bookingId, setBookingId] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const runTest = async () => {
    if (!bookingId.trim()) {
      toast.error('Inserisci un ID prenotazione');
      return;
    }
    
    setLoading(true);
    setTestResult(null);
    
    try {
      const response = await fetch(`${API}/admin/communication/routing/test`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ booking_id: bookingId.trim() })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setTestResult(data);
        toast.success('Test routing completato');
      } else {
        toast.error(data.detail || 'Errore nel test');
      }
    } catch (error) {
      toast.error('Errore di rete');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
        <Play className="w-4 h-4 text-blue-500" />
        Test Routing
      </h3>
      
      <div className="flex gap-2 mb-4">
        <input
          type="text"
          value={bookingId}
          onChange={(e) => setBookingId(e.target.value)}
          placeholder="ID Prenotazione (es: abc123-def456)"
          className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
        />
        <button
          onClick={runTest}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium disabled:opacity-50"
        >
          {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          Test
        </button>
      </div>
      
      {testResult && (
        <div className="bg-gray-50 rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <p className="font-medium text-gray-900">{testResult.guest_name || 'Ospite'}</p>
            <span className={`px-2 py-0.5 text-xs rounded-full ${
              testResult.routing_decision?.result === 'success' ? 'bg-green-100 text-green-700' :
              testResult.routing_decision?.result === 'fallback_used' ? 'bg-amber-100 text-amber-700' :
              'bg-red-100 text-red-700'
            }`}>
              {testResult.routing_decision?.result}
            </span>
          </div>
          
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div>
              <p className="text-xs text-gray-500">Canale Selezionato</p>
              <p className="font-medium">{testResult.routing_decision?.selected_channel || 'Nessuno'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Destinazione</p>
              <p className="font-mono text-sm">{testResult.routing_decision?.destination_masked || 'N/A'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Fonte Prenotazione</p>
              <p className="font-medium">{testResult.contact_info?.booking_source || 'N/A'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Fallback Chain</p>
              <p className="font-mono text-xs">{testResult.routing_decision?.fallback_chain?.join(' → ') || 'N/A'}</p>
            </div>
          </div>
          
          {testResult.routing_decision?.error_message && (
            <div className="flex items-start gap-2 p-2 bg-red-50 rounded-lg">
              <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{testResult.routing_decision.error_message}</p>
            </div>
          )}
          
          <p className="text-xs text-gray-400 text-center">
            Test mode - Nessun messaggio inviato
          </p>
        </div>
      )}
    </div>
  );
};

// Main Page
export default function RoutingPage() {
  const { token } = useAuth();
  const [policy, setPolicy] = useState(null);
  const [channelsStatus, setChannelsStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [policyRes, channelsRes] = await Promise.all([
        fetch(`${API}/admin/communication/routing/policy`, { headers: { Authorization: `Bearer ${token}` }}),
        fetch(`${API}/admin/communication/channels/status`, { headers: { Authorization: `Bearer ${token}` }})
      ]);
      
      if (policyRes.ok) {
        const data = await policyRes.json();
        setPolicy(data.policy);
      }
      
      if (channelsRes.ok) {
        const data = await channelsRes.json();
        setChannelsStatus(data.channels);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchData();
  }, [token]);

  const updateChannelPriority = (index, updates) => {
    const newPolicy = { ...policy };
    newPolicy.channel_priority = [...newPolicy.channel_priority];
    newPolicy.channel_priority[index] = { ...newPolicy.channel_priority[index], ...updates };
    setPolicy(newPolicy);
  };

  const moveChannel = (index, direction) => {
    const newPolicy = { ...policy };
    const channels = [...newPolicy.channel_priority];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex < 0 || targetIndex >= channels.length) return;
    
    // Swap priorities
    const tempPriority = channels[index].priority;
    channels[index].priority = channels[targetIndex].priority;
    channels[targetIndex].priority = tempPriority;
    
    // Sort by priority
    channels.sort((a, b) => a.priority - b.priority);
    
    newPolicy.channel_priority = channels;
    setPolicy(newPolicy);
  };

  const savePolicy = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/admin/communication/routing/policy`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(policy)
      });
      
      if (response.ok) {
        toast.success('Routing policy salvata');
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Errore nel salvataggio');
      }
    } catch (error) {
      toast.error('Errore di rete');
    } finally {
      setSaving(false);
    }
  };

  const sortedChannels = policy?.channel_priority?.sort((a, b) => a.priority - b.priority) || [];

  return (
    <AdminLayout title="Channel Routing">
      <div className="p-4 md:p-6 space-y-6 max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Navigation className="w-6 h-6 text-blue-600" />
              Channel Routing
            </h1>
            <p className="text-gray-500 mt-1">Configura priorità canali e politica di fallback</p>
          </div>
          <button
            onClick={savePolicy}
            disabled={saving || loading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Salvataggio...' : 'Salva Policy'}
          </button>
        </div>

        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-20 bg-gray-200 rounded-xl animate-pulse"></div>
            ))}
          </div>
        ) : (
          <div className="space-y-6">
            {/* Channels Status */}
            <ChannelsStatusPanel channelsStatus={channelsStatus} />
            
            {/* Channel Priority */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Settings className="w-4 h-4 text-gray-500" />
                  Priorità Canali
                </h3>
                <p className="text-xs text-gray-500">Trascina per riordinare • Prima = Priorità più alta</p>
              </div>
              
              <div className="space-y-2">
                {sortedChannels.map((ch, index) => (
                  <ChannelPriorityCard
                    key={ch.channel}
                    channel={ch.channel}
                    priority={ch.priority}
                    enabled={ch.enabled}
                    fallbackEnabled={ch.fallback_enabled}
                    onUpdate={(updates) => updateChannelPriority(index, updates)}
                    onMoveUp={() => moveChannel(index, 'up')}
                    onMoveDown={() => moveChannel(index, 'down')}
                    canMoveUp={index > 0}
                    canMoveDown={index < sortedChannels.length - 1}
                  />
                ))}
              </div>
              
              {/* Routing Flow Visualization */}
              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-xs text-gray-500 mb-2">Flusso Routing:</p>
                <div className="flex items-center flex-wrap gap-2">
                  {sortedChannels.filter(c => c.enabled).map((ch, idx, arr) => {
                    const Icon = CHANNEL_ICONS[ch.channel] || MessageSquare;
                    return (
                      <div key={ch.channel} className="flex items-center gap-2">
                        <div className={`flex items-center gap-1 px-2 py-1 rounded-lg text-sm ${CHANNEL_COLORS[ch.channel] || 'bg-gray-100'}`}>
                          <Icon className="w-4 h-4" />
                          <span>{CHANNEL_LABELS[ch.channel]}</span>
                        </div>
                        {idx < arr.length - 1 && (
                          <ArrowRight className="w-4 h-4 text-gray-400" />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            
            {/* Global Settings */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Shield className="w-4 h-4 text-gray-500" />
                Impostazioni Globali
              </h3>
              
              <div className="grid grid-cols-2 gap-4">
                <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <input
                    type="checkbox"
                    checked={policy?.fallback_enabled ?? true}
                    onChange={(e) => setPolicy({...policy, fallback_enabled: e.target.checked})}
                    className="w-4 h-4 rounded border-gray-300 text-blue-600"
                  />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Fallback Abilitato</p>
                    <p className="text-xs text-gray-500">Passa al canale successivo se il primo fallisce</p>
                  </div>
                </label>
                
                <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <input
                    type="checkbox"
                    checked={policy?.retry_before_fallback ?? true}
                    onChange={(e) => setPolicy({...policy, retry_before_fallback: e.target.checked})}
                    className="w-4 h-4 rounded border-gray-300 text-blue-600"
                  />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Retry Prima di Fallback</p>
                    <p className="text-xs text-gray-500">Riprova prima di passare al canale successivo</p>
                  </div>
                </label>
                
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm font-medium text-gray-900 mb-1">Max Tentativi Fallback</p>
                  <select
                    value={policy?.max_fallback_attempts ?? 3}
                    onChange={(e) => setPolicy({...policy, max_fallback_attempts: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  >
                    {[1, 2, 3, 4, 5].map(n => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                </div>
                
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm font-medium text-gray-900 mb-1">Retry Prima di Fallback</p>
                  <select
                    value={policy?.retry_attempts_before_fallback ?? 2}
                    onChange={(e) => setPolicy({...policy, retry_attempts_before_fallback: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  >
                    {[1, 2, 3, 4, 5].map(n => (
                      <option key={n} value={n}>{n} tentativi</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            
            {/* Routing Test */}
            <RoutingTestPanel token={token} />
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
